import { Tracker } from '@rehaje/reactive'
import { useRef } from 'react'
import { useSyncExternalStore } from 'use-sync-external-store/shim'

import { internalsCanIUse, internalsIsRendering } from '../internals'

import type { IExoticFC } from '../types'
import type { Callback } from '@rehaje/shared'
import type { ReactNode } from 'react'

interface Observer {
  getSnapshot: Callback
  idleUpdate: Callback
  onStoreChange?: Callback
  schedulerUpdate: Callback
  subscribe: (onStoreChange: Callback) => Callback
  syncUpdate: Callback
  tracker?: Tracker
  version: number
}

const finalization = new FinalizationRegistry((obs: Observer) => {
  obs.tracker?.dispose()
  obs.tracker = undefined
})

let idleUpdateQueue: Observer[] = []
let idleCallback: null | number = null

export function useObserver<T = any, P = any>(
  render: IExoticFC<T, P>,
  props = {} as P,
  ref?: T,
  scheduler: (update: Callback) => void = (update) => update()
): ReactNode {
  const obsRef = useRef<Observer>()
  if (obsRef.current === undefined) {
    const obs: Observer = {
      getSnapshot: () => obs.version,
      idleUpdate: () => {
        if (!internalsIsRendering()) return obs.syncUpdate()
        if (idleUpdateQueue.includes(obs)) return
        idleUpdateQueue.push(obs)
        idleCallback ??= requestIdleCallback(() => {
          const updateQueue = idleUpdateQueue
          idleUpdateQueue = []
          idleCallback = null
          for (const obs of updateQueue) obs.syncUpdate()
        })
      },
      onStoreChange: undefined,
      schedulerUpdate: () => {
        if (internalsCanIUse) scheduler(obs.idleUpdate)
        else scheduler(obs.syncUpdate)
      },
      subscribe: (onStoreChange: Callback) => {
        finalization.unregister(obs)
        obs.onStoreChange = onStoreChange
        if (obs.tracker === undefined) {
          obs.tracker = new Tracker(obs.schedulerUpdate)
          obs.schedulerUpdate()
        }
        return () => {
          obs.onStoreChange = undefined
          obs.tracker?.dispose()
          obs.tracker = undefined
        }
      },
      syncUpdate: () => {
        obs.version++
        obs.onStoreChange?.()
      },
      tracker: undefined,
      version: 0
    }
    obsRef.current = obs
  }

  const obs = obsRef.current
  if (obs.tracker === undefined) {
    obs.tracker = new Tracker(obs.schedulerUpdate)
    finalization.register(obsRef, obs, obs)
  }

  useSyncExternalStore(obs.subscribe, obs.getSnapshot)

  let error, result
  let hasError = false
  obs.tracker.track(() => {
    try {
      result = render(props, ref)
    } catch (error_) {
      error = error_
      hasError = true
    }
  })

  if (hasError) {
    throw error
  }

  return result
}
